import type {
  CoachingPartnership,
  CoachingPartnershipMessage,
  CoachingPartnershipStatus,
  CoachProfile,
} from '@efdigital/ll-addon-data';
import { tracked } from '@glimmer/tracking';

/**
 * Ops matching creates a `pending` partnership row for coaches in the browse
 * pool. Those must never become message threads — only shortlisted or chosen
 * coaches may appear in the inbox.
 */
const NON_MESSAGEABLE_PARTNERSHIP_STATUSES: CoachingPartnershipStatus[] = [
  'pending',
  'declined',
  'reassigned',
];

/** Statuses a coach may message across — active coaching relationships only. */
export const COACH_MESSAGEABLE_PARTNERSHIP_STATUSES: CoachingPartnershipStatus[] =
  ['chemistry', 'chosen'];

export function messageablePartnershipStatusesForCoachee(
  coachHasBeenChosen: boolean
): CoachingPartnershipStatus[] {
  // Once a coach is chosen, stale chemistry rows stay in the plan; messaging
  // should follow the chosen partnership only.
  return coachHasBeenChosen ? ['chosen'] : ['chemistry'];
}

export function isMessageablePartnershipStatus(
  status: CoachingPartnershipStatus,
  role: MessagingRole,
  coachHasBeenChosen = false
): boolean {
  if (NON_MESSAGEABLE_PARTNERSHIP_STATUSES.includes(status)) {
    return false;
  }

  const allowedStatuses =
    role === 'coach'
      ? COACH_MESSAGEABLE_PARTNERSHIP_STATUSES
      : messageablePartnershipStatusesForCoachee(coachHasBeenChosen);

  return allowedStatuses.includes(status);
}

export function messageablePartnerships(
  partnerships: readonly CoachingPartnership[],
  role: MessagingRole,
  coachHasBeenChosen = false
): CoachingPartnership[] {
  return partnerships.filter((partnership) =>
    isMessageablePartnershipStatus(partnership.status, role, coachHasBeenChosen)
  );
}

export type MessagingRole = 'coach' | 'coachee';

export type MessageParticipant = {
  firstName: string;
  initials: string;
  name: string;
  secondary: string;
  /** Set on the coachee thread so the UI can show the coach photo. */
  coach?: CoachProfile;
};

export class PartnershipConversation {
  partnership: CoachingPartnership;
  participant: MessageParticipant;
  @tracked messages: CoachingPartnershipMessage[];

  constructor({
    partnership,
    participant,
    messages,
  }: {
    partnership: CoachingPartnership;
    participant: MessageParticipant;
    messages: CoachingPartnershipMessage[];
  }) {
    this.partnership = partnership;
    this.participant = participant;
    this.messages = messages;
  }
}

export function sortMessages(
  messages: readonly CoachingPartnershipMessage[]
): CoachingPartnershipMessage[] {
  return [...messages].sort(
    (left, right) =>
      new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()
  );
}

export function latestMessage(
  conversation: PartnershipConversation
): CoachingPartnershipMessage | undefined {
  return conversation.messages.at(-1);
}

/** Most recent conversation first, using each thread's newest message.
 *  Conversations with no messages yet sort to the end. */
export function sortConversationsByRecency(
  conversations: readonly PartnershipConversation[]
): PartnershipConversation[] {
  return [...conversations].sort((left, right) => {
    const leftCreatedAt = latestMessage(left)?.createdAt;
    const rightCreatedAt = latestMessage(right)?.createdAt;

    if (!leftCreatedAt && !rightCreatedAt) {
      return 0;
    }
    if (!leftCreatedAt) {
      return 1;
    }
    if (!rightCreatedAt) {
      return -1;
    }

    return (
      new Date(rightCreatedAt).getTime() - new Date(leftCreatedAt).getTime()
    );
  });
}

export function conversationPreview(
  conversation: PartnershipConversation
): string {
  return latestMessage(conversation)?.body ?? 'No messages yet';
}

export function incomingUnreadMessages(
  conversation: PartnershipConversation,
  role: MessagingRole,
  currentUserId: string
): CoachingPartnershipMessage[] {
  return conversation.messages.filter(
    (message) =>
      message.senderUserId !== currentUserId &&
      (role === 'coach'
        ? message.readByCoachAt === null
        : message.readByCoacheeAt === null)
  );
}

export function hasUnreadMessages(
  conversation: PartnershipConversation,
  role: MessagingRole,
  currentUserId: string
): boolean {
  return unreadMessageCount(conversation, role, currentUserId) > 0;
}

export function unreadMessageCount(
  conversation: PartnershipConversation,
  role: MessagingRole,
  currentUserId: string
): number {
  return incomingUnreadMessages(conversation, role, currentUserId).length;
}

export function totalUnreadMessageCount(
  conversations: readonly PartnershipConversation[],
  role: MessagingRole,
  currentUserId: string
): number {
  return conversations.reduce(
    (total, conversation) =>
      total + unreadMessageCount(conversation, role, currentUserId),
    0
  );
}

export function formatMessageDate(value: string): string {
  const date = new Date(value);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();

  return new Intl.DateTimeFormat('en-US', {
    ...(sameDay
      ? { hour: 'numeric', minute: '2-digit' }
      : { month: 'short', day: 'numeric' }),
  }).format(date);
}

export function formatMessageTimestamp(value: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

export function initials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}
